import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

export interface AuthUser {
  id: string
  name: string
  avatar: string
  role: 'owner' | 'admin' | 'moderator' | 'strategist' | 'member'
}

interface AuthContextValue {
  user: AuthUser | null
  loading: boolean
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  logout: async () => {},
})

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch('/api/auth/me', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(data => setUser(data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false))
  }, [])

  // Ping every 60s to update online status
  useEffect(() => {
    if (!user) return
    const ping = () => fetch('/api/online/ping', { method: 'POST', credentials: 'include' })
    ping()
    const id = setInterval(ping, 60_000)
    return () => clearInterval(id)
  }, [user])

  const logout = async () => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, loading, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}

// Roles: member < moderator < admin. 'owner' is granted by discord ID in the
// backend and is not assignable from the UI.
//
// moderator and admin currently carry identical permissions — mirrors
// PRIVILEGED_ROLES in backend/auth/deps.py. isOperator/isAdmin are kept as
// separate names so a real split can be introduced by narrowing one set.
export const PRIVILEGED_ROLES = new Set(['owner', 'admin', 'moderator'])

export function isAdmin(user: AuthUser | null) {
  return !!user && PRIVILEGED_ROLES.has(user.role)
}

export function isOperator(user: AuthUser | null) {
  return !!user && PRIVILEGED_ROLES.has(user.role)
}

// The one place the privileged roles genuinely differ: creating, editing and
// deleting matches is the admin team's job, moderators excluded. Mirrors
// MANAGER_ROLES in backend/auth/deps.py.
export const MANAGER_ROLES = new Set(['owner', 'admin'])

export function isManager(user: AuthUser | null) {
  return !!user && MANAGER_ROLES.has(user.role)
}

// "Strateji Ekibi" (shown as "Strateji"): builds rosters and match strategies
// but may not publish/share them. Not an operator — mirrors STRATEGY_TEAM_ROLES
// in backend/auth/deps.py. Use isStrategyTeam to gate roster/match-strategy
// *building*; keep isOperator on the send/publish actions.
export const STRATEGY_TEAM_ROLES = new Set(['owner', 'admin', 'moderator', 'strategist'])

export function isStrategyTeam(user: AuthUser | null) {
  return !!user && STRATEGY_TEAM_ROLES.has(user.role)
}
